import React, { useEffect, useState } from 'react';
import { Button, Header, Icon, List, Segment } from 'semantic-ui-react';
import { getTranslations, format } from './translations';
import { cardStyle, headerRowStyle, iconButtonStyle } from './StarterPanel';

/**
 * FirstSessionChecklist: the compact first-session steps. Non-modal, collapsible and dismissible.
 * Starts collapsed to a pill on mobile or when the lab has no room for it (`compact`).
 */
const FirstSessionChecklist = ({ stepIds, steps, current_locale, isMobile, compact, onDismiss }) => {
  const [collapsed, setCollapsed] = useState(!!isMobile || !!compact);
  // No room (small window, zoom, the guest CTA below): fold to the pill; the user can still open it
  useEffect(() => {
    if (isMobile || compact) setCollapsed(true);
  }, [isMobile, compact]);
  const t = getTranslations(current_locale);
  const done = stepIds.filter((id) => steps[id]).length;
  const pillText = format(t.pill, { done, total: stepIds.length });

  const handleToggle = () => setCollapsed((value) => !value);

  if (collapsed) {
    return (
      <Button
        id="first-session-checklist-pill"
        type="button"
        size="small"
        aria-expanded={false}
        onClick={handleToggle}
        style={{
          margin: 0,
          flexShrink: 0,
          pointerEvents: 'auto',
          borderRadius: 999,
          color: 'var(--card-foreground)',
          background: 'var(--card)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-xl)'
        }}
      >
        <Icon name="tasks" />
        {pillText}
      </Button>
    );
  }

  return (
    <Segment id="first-session-checklist" role="region" style={cardStyle} aria-labelledby="first-session-checklist-title">
      <div style={headerRowStyle}>
        <Header as="h4" id="first-session-checklist-title" style={{ margin: 0, flex: 1, color: 'var(--card-foreground)', fontSize: '0.95rem' }}>
          {t.checklistTitle}
          <span style={{ marginInlineStart: 6, fontWeight: 400, color: 'var(--muted-foreground)', fontSize: '0.85rem' }}>
            {done}/{stepIds.length}
          </span>
        </Header>
        <Button
          id="first-session-checklist-toggle"
          type="button"
          icon="chevron up"
          size="mini"
          basic
          style={iconButtonStyle}
          aria-expanded
          aria-controls="first-session-checklist-steps"
          aria-label={t.collapse}
          onClick={handleToggle}
        />
        <Button
          id="first-session-checklist-close"
          type="button"
          icon="close"
          size="mini"
          basic
          style={iconButtonStyle}
          aria-label={t.closeChecklist}
          onClick={onDismiss}
        />
      </div>
      <List id="first-session-checklist-steps" style={{ margin: '8px 0 0' }}>
        {stepIds.map((id) => {
          const isDone = !!steps[id];
          return (
            <List.Item key={id} id={`first-session-step-${id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
              <Icon
                name={isDone ? 'check square' : 'square outline'}
                style={{ margin: 0, color: isDone ? '#21ba45' : 'var(--muted-foreground)' }}
                aria-hidden="true"
              />
              <span
                style={{
                  fontSize: '0.9rem',
                  color: isDone ? 'var(--muted-foreground)' : 'var(--card-foreground)',
                  textDecoration: isDone ? 'line-through' : 'none'
                }}
              >
                {t.steps[id]}
              </span>
              <span className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                {isDone ? t.stepDone : t.stepPending}
              </span>
            </List.Item>
          );
        })}
      </List>
    </Segment>
  );
};

export default FirstSessionChecklist;
